import type {
  ApiExportRequest,
  ApiListingFiltersOnlyRequest,
  ApiListingRequest,
  ApiResponse,
} from '@bitstopco/bitstop-theme';

import api from '@/store/api';
import { athena, unity } from '@/store/api/instances';

import type {
  RedemptionSettlement,
  TransactionDetail,
  TransactionImages,
  TransactionsSale,
  TransactionsStats,
} from '@/types';

/**
 * Transactions RTK-Query API
 * For more information about RTK-Query see
 * * https://redux-toolkit.js.org/rtk-query/overview
 */
export const transactionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTransaction: build.query<ApiResponse<TransactionDetail>, string>({
      query: (id) =>
        athena({
          url: `v2/transaction/${id}`,
        }),
      providesTags: (_, __, id) => [{ type: 'Transaction', id }, 'Transaction'],
    }),
    getTransactionImages: build.query<ApiResponse<TransactionImages>, string>({
      query: (id) =>
        athena({
          url: `v2/transaction/${id}/images`,
        }),
      providesTags: (_, __, id) => [{ type: 'TransactionImages', id }],
    }),
    getTransactions: build.query<ApiResponse<TransactionsSale[]>, ApiListingRequest>({
      query: (data) =>
        athena({
          url: 'v2/transactions',
          method: 'POST',
          data,
        }),
      providesTags: (response) => {
        const result = response?.data || [];
        return [
          ...result.map(({ id }) => ({ type: 'Transactions', id }) as const),
          { type: 'Transactions' as const, id: 'LIST' },
        ];
      },
    }),
    getTransactionsStats: build.query<ApiResponse<TransactionsStats>, ApiListingFiltersOnlyRequest>(
      {
        query: (data) =>
          athena({
            url: 'v2/transactions/stats',
            method: 'POST',
            data,
          }),
      },
    ),
    getTransactionsExport: build.mutation<BlobPart, ApiExportRequest>({
      query: ({ type, data }) => athena({ url: `v2/transactions/${type}`, method: 'POST', data }),
    }),
    unfreezeTransaction: build.mutation<void, string>({
      query: (id) => unity({ url: `sales/${id}/reinstate`, method: 'POST' }),
    }),
    refundTransaction: build.mutation<void, string>({
      query: (id) => unity({ url: `sales/${id}/refund`, method: 'POST' }),
    }),
    manuallyFreezeTransaction: build.mutation<void, string>({
      query: (id) => unity({ url: `sales/${id}/manually_freeze`, method: 'POST' }),
    }),
    issueRedemption: build.mutation<
      ApiResponse<RedemptionSettlement[]>,
      { saleId: string; obligationId: string; assetAmount: number; method: string; notes?: string }
    >({
      query: ({ obligationId, assetAmount, method }) =>
        unity({
          url: `redemptions/${obligationId}/settlements`,
          method: 'POST',
          data: [{ method, asset_amount: assetAmount }],
        }),
      invalidatesTags: (_, __, { saleId }) => [{ type: 'Transaction', id: saleId }, 'Transaction'],
    }),
    updateRedemptionSettlementNotes: build.mutation<
      void,
      { saleId: string; settlementId: string; notes: string }
    >({
      query: ({ settlementId, notes }) =>
        unity({
          url: `redemptions/settlements/${settlementId}`,
          method: 'PUT',
          data: { notes },
        }),
      invalidatesTags: (_, __, { saleId }) => [{ type: 'Transaction', id: saleId }, 'Transaction'],
    }),
  }),
});

export const {
  useGetTransactionQuery,
  useGetTransactionsQuery,
  useGetTransactionImagesQuery,
  useGetTransactionsStatsQuery,
  useGetTransactionsExportMutation,
  useUnfreezeTransactionMutation,
  useRefundTransactionMutation,
  useManuallyFreezeTransactionMutation,
  useLazyGetTransactionsStatsQuery,
  useIssueRedemptionMutation,
  useUpdateRedemptionSettlementNotesMutation,
} = transactionsApi;

export const {
  endpoints: {
    getTransaction,
    getTransactions,
    getTransactionImages,
    getTransactionsStats,
    getTransactionsExport,
    unfreezeTransaction,
    refundTransaction,
    manuallyFreezeTransaction,
  },
} = transactionsApi;
